import { useHybridData } from "./useHybridData";
import { supabase } from "@/integrations/supabase/client";
import type { Database } from "@/integrations/supabase/types";
import testimonialIndex from "@/data/generated/testimonials-index.json";

export interface Testimonial {
  id: string;
  name: string;
  location: string | null;
  treatment: string | null;
  rating: number | null;
  quote: string | null;
  image: string | null;
  is_featured: boolean | null;
}

const fetchNewTestimonials = async (lastBuildDate: string) => {
  const { data, error } = await supabase
    .from("testimonials")
    .select("*")
    .gt("created_at", lastBuildDate)
    .order("rating", { ascending: false });

  if (error) {
    console.error("Error fetching new testimonials:", error);
    return null;
  }
  
  // Map raw DB fields to our standardized Testimonial interface
  const rows = (data ?? []) as Database["public"]["Tables"]["testimonials"]["Row"][];
  return rows.map((t) => ({
    id: t.id,
    name: t.patient_name,
    location: t.country,
    quote: t.content,
    rating: t.rating,
    treatment: null, // Dynamic updates might miss the join, acceptable for now or could fetch
    image: t.image_url,
    is_featured: t.is_featured
  })) as Testimonial[];
};

export function useTestimonials() {
  const { data, isStatic, isCheckingUpdates } = useHybridData<Testimonial>({
    resourceName: "Testimonials",
    staticData: testimonialIndex as unknown as Testimonial[],
    fetchDynamic: fetchNewTestimonials,
    queryKey: ["testimonials"],
    sortFn: (a, b) => (b.rating || 0) - (a.rating || 0),
    skipUpdates: true,
  });

  return {
    data,
    isLoading: false,
    isStatic,
    isCheckingUpdates
  };
}
